import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, filter } from 'rxjs';
import { map } from 'rxjs/operators';
import { apiUrl } from './api-url';
import { AuthService } from './auth.service';

export interface PaymentGateway {
  id: string;
  name: string;
  type: 'bank' | 'mobile_money';
}

export interface Currency {
  code: string;
  name: string;
  country: string;
  countries?: string[];
  flag: string;
  gateways: PaymentGateway[];
}

export interface Theme {
  primary: string;
  on_primary: string;
  primary_container: string;
  on_primary_container: string;
  secondary: string;
  on_secondary: string;
  secondary_container: string;
  on_secondary_container: string;
  surface: string;
  on_surface: string;
  surface_variant: string;
  on_surface_variant: string;
  background: string;
  on_background: string;
  error: string;
  on_error: string;
  outline: string;
  outline_variant: string;
}

export interface FeatureFlags {
  backoffice_team_service: 'none' | 'mock' | 'real';
  manual_settlement: string;
  organisations_service: 'none' | 'mock' | 'real';
  payment_gateways_service: 'none' | 'mock' | 'real';
  currency_settings_service: 'none' | 'mock' | 'real';
  custom_fees_service: 'none' | 'mock' | 'real';
  payment_methods_service: 'none' | 'mock' | 'real';
  markets_service: 'none' | 'mock' | 'real';
  audit_trails_service: 'none' | 'mock' | 'real';
  process_codes_service: 'none' | 'mock' | 'real';
  wallet_service: 'none' | 'mock' | 'real';
  cwr_service: 'none' | 'mock' | 'real';
  org_team_service: 'none' | 'mock' | 'real';
  org_payment_methods_service: 'none' | 'mock' | 'real';
  deposits_service: 'none' | 'mock' | 'real';
  withdrawals_service: 'none' | 'mock' | 'real';
  transactions_service: 'none' | 'mock' | 'real';
  two_fa: 'required' | 'none';
  passkey: 'none' | 'real';
  email_notifications_service: 'none' | 'mock' | 'real';
  org_deposit_upload: 'none' | 'required';
  backoffice_deposit_upload: 'none' | 'required';
  backoffice_withdrawal_upload: 'none' | 'required';
  deposit_exception_service: 'none' | 'mock' | 'real';
  withdrawal_exception_service: 'none' | 'mock' | 'real';
  instant_offers_service: 'none' | 'mock' | 'real';
  trades_service: 'none' | 'mock' | 'real';
  revenue_share_service: 'none' | 'mock' | 'real';
  easy_convert_service: 'none' | 'mock' | 'real';
  otc_convert_service?: 'none' | 'mock' | 'real';
  fx_convert_service?: 'none' | 'mock' | 'real';
  otc_wallet_refund?: 'none' | 'real';
  data_maintenance?: 'none' | 'real';
  indicative_offers_service?: 'none' | 'mock' | 'real';
  indicative_interventions?: 'none' | 'real';
  indicative_offer_notifications?: 'none' | 'real';
  dashboard_service?: 'none' | 'mock' | 'real';
  org_api_keys_service: 'none' | 'mock' | 'real';
  management_reports_service?: 'none' | 'mock' | 'real';
  system_logs_service?: 'none' | 'mock' | 'real';
  bo_org_teams_service?: 'none' | 'mock' | 'real';
  bo_exchange_service?: 'none' | 'mock' | 'real';
  bo_market_making_service?: 'none' | 'mock' | 'real';
  bo_indicative_offers_service?: 'none' | 'mock' | 'real';
  bo_indicative_settlements_service?: 'none' | 'mock' | 'real';
  bo_api_keys_service?: 'none' | 'mock' | 'real';
  org_docs_service?: 'none' | 'real';
  privacy_policy_service?: 'none' | 'mock' | 'real';
  landing_page?: 'none' | 'real';
  retail_service?: 'none' | 'mock' | 'real';
  kyc_service?: 'none' | 'mock' | 'real';
  financial_institutions_service?: 'none' | 'mock' | 'real';
  profile_service?: 'none' | 'mock' | 'real';
  system_monitor_service?: 'none' | 'mock' | 'real';
  announcements_service?: 'none' | 'mock' | 'real';
  broadcasts_service?: 'none' | 'mock' | 'real';
  impersonation_service?: 'none' | 'real';
  multi_org_membership?: 'none' | 'mock' | 'real';
  download_center_service?: 'none' | 'mock' | 'real';
  org_roles_service?: 'none' | 'mock' | 'real';
  gateway_reconciliation_service?: 'none' | 'mock' | 'real';
  currency_equivalence?: 'none' | 'real';
  blockchain_admin_service?: 'none' | 'mock' | 'real';
  blockchain_faucet_service?: 'none' | 'mock' | 'real';
  crypto_payment_method?: 'none' | 'real';
  theme_toggle?: 'none' | 'real';
  global_currencies?: 'none' | 'real';
  auth_provider?: 'internal' | 'keycloak';
  keycloak_allow_purge?: boolean;
  balance_migration_service?: 'none' | 'manual' | 'auto';
  deposit_account_selection?: 'none' | 'real';
  // Dual-control (maker/checker) refactor. 'real' (default) = no admin role / no super admin,
  // maker+checker + 2-user org creation; 'none' = legacy admin model. The backend normalizes
  // the legacy 'enforced' alias to 'real', so the frontend only ever receives 'real' | 'none'.
  dual_control?: 'real' | 'none';
  retail_remittance_service?: 'none' | 'mock' | 'real';
  remittance_revenue_share_service?: 'none' | 'mock' | 'real';
}

export type Environment = 'dev' | 'staging' | 'sandbox' | 'preprod' | 'prod';

export interface AppConfig {
  project_name: string;
  // Which deployment this is. Present on both public-config and config responses so the
  // real-money banner shows even on the pre-login page. Optional in the type because a config
  // cached in localStorage from before this field existed won't have it yet, until the next
  // HTTP fetch overwrites the cache - the banner treats a missing value as "don't show".
  environment?: Environment;
  theme: Theme;
  dark_theme?: Theme;
  currencies: Currency[];
  feature_flags: FeatureFlags;
  featured_currency?: string;
  supported_crypto_networks?: { id: string; name: string }[];
  branding_profile?: string;
  app_logo_url?: string;
  hero_headline?: string;
  hero_subtext?: string;
  hero_partner_logos?: string[];
  keycloak?: {
    url: string;
    realm: string;
    client_id: string;
    organization_scope?: string;
  };
  // Platform-wide OTC Convert default fee percentage. Backoffice-only - only present on the
  // authenticated /config response, never on the unauthenticated public-config. Backend keeps
  // decimal.Decimal amounts as quoted JSON strings, so this is a string despite being a number.
  otc_convert_fee_percentage?: string;
  // True only on the authenticated /config response, absent on public-config. Switcher services
  // that need a feature flag public-config structurally omits (rather than zero-valuing) must
  // filter on this before take(1) — otherwise they can resolve off the narrower pre-login config
  // and never re-check once the authenticated response arrives.
  authenticated?: boolean;
}

interface JsonApiResponse {
  data: {
    type: string;
    id: string;
    attributes: AppConfig;
  };
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private static readonly CACHE_KEY = 'app_config_cache';

  // Set to true once loadFullConfig() succeeds — prevents the narrower public-config
  // response from overwriting the richer post-login config.
  private hasFullConfig = false;
  // Guards against multiple publicConfig$ subscriptions across repeated getConfig() calls.
  private publicConfigSubscribed = false;
  // configReadyGuard calls loadFullConfig() on every dashboard-shell activation - collapse
  // overlapping calls into one request instead of stacking round trips.
  private fullConfigInFlight = false;

  // Holds the latest config. Starts with whatever is in localStorage so the app
  // renders immediately without waiting for an HTTP round-trip.
  private config$ = new BehaviorSubject<AppConfig | null>(this.getCachedConfig());

  /** Returns an Observable that emits the current config and stays open for updates. */
  getConfig(): Observable<AppConfig> {
    // Always fire the public-config fetch on first call — even if a cached value
    // exists — so security-relevant flags (two_fa, auth_provider) are kept fresh.
    // The cache provides an immediate synchronous emission; the HTTP response updates
    // config$ in the background.
    this.fetchPublicConfig();
    return this.config$.pipe(filter((cfg): cfg is AppConfig => cfg !== null));
  }

  /**
   * Synchronous check for the dual-control (maker/checker) world. Returns true unless the
   * backend explicitly reports dual_control === 'none' (legacy admin model). Use this to gate
   * the admin role picker and the 2-user org creation form. Do NOT conflate with
   * org_roles_service (rpa/tpa/corporate).
   */
  dualControlEnforced(): boolean {
    return this.config$.value?.feature_flags?.dual_control !== 'none';
  }

  // Fetches the unauthenticated public config exactly once and pushes it into config$.
  // A brand-new visitor has no cached config, so config$ starts null and the very first
  // emission depends on this fetch succeeding. If it fails, reset the guard so the next
  // getConfig() call retries — otherwise the visitor stays wedged on a permanently-null
  // config and the app never renders past a blank page.
  private fetchPublicConfig(): void {
    if (this.publicConfigSubscribed) return;
    this.publicConfigSubscribed = true;
    this.http
      .get<JsonApiResponse>(apiUrl('public-config'))
      .pipe(map((res) => res.data.attributes))
      .subscribe({
        next: (cfg) => {
          const next = this.mergeOntoAuthenticatedConfig(cfg);
          this.saveToCache(next);
          // Don't overwrite the richer post-login config with the narrower public one.
          if (!this.hasFullConfig) {
            this.config$.next(next);
          }
        },
        error: () => {
          // Allow a retry on the next call instead of wedging on the first failure.
          this.publicConfigSubscribed = false;
        },
      });
  }

  /**
   * Fetches the full authenticated config and updates the config state.
   * Call this once after the user has logged in so all feature flags and
   * gateway IDs become available to switcher services.
   */
  loadFullConfig(): void {
    if (this.fullConfigInFlight) return;
    this.fullConfigInFlight = true;
    const session = this.authService.getSession();
    const headers = new HttpHeaders({ Authorization: `Bearer ${session?.id ?? ''}` });
    this.http
      .get<JsonApiResponse>(apiUrl('config'), { headers })
      .pipe(map((res) => res.data.attributes))
      .subscribe({
        next: (cfg) => {
          this.fullConfigInFlight = false;
          this.hasFullConfig = true;
          this.saveToCache(cfg);
          this.config$.next(cfg);
        },
        error: () => {
          // Allow a retry on the next activation instead of wedging on one failure.
          this.fullConfigInFlight = false;
        },
      });
  }

  // The only feature flags public-config carries - everything else it structurally omits
  // rather than zero-valuing (see loginFeatureFlags in the backend's config/controllers.go).
  private static readonly LOGIN_FLAG_KEYS = [
    'two_fa',
    'passkey',
    'auth_provider',
    'retail_service',
    'retail_remittance_service',
  ] as const;

  /**
   * Reconciles a public-config response with an already-authenticated config.
   *
   * `hasFullConfig` is in-memory, so it is false on every page load - which used to let the
   * narrow public-config response replace a cached authenticated config wholesale. That dropped
   * every flag public-config omits (switcher services read the absence as 'mock') along with the
   * gateway IDs public-config strips from each currency. Overlay only the login flags instead, so
   * the security-relevant ones (two_fa, auth_provider) still refresh on every startup.
   */
  private mergeOntoAuthenticatedConfig(incoming: AppConfig): AppConfig {
    const current = this.config$.value;
    if (current?.authenticated !== true) return incoming;

    const flags: FeatureFlags = { ...current.feature_flags };
    for (const key of ConfigService.LOGIN_FLAG_KEYS) {
      const value = incoming.feature_flags?.[key];
      if (value !== undefined) Object.assign(flags, { [key]: value });
    }
    return { ...current, feature_flags: flags };
  }

  private saveToCache(config: AppConfig): void {
    try {
      localStorage.setItem(ConfigService.CACHE_KEY, JSON.stringify(config));
    } catch {
      /* quota exceeded or unavailable */
    }
  }

  private getCachedConfig(): AppConfig | null {
    try {
      const raw = localStorage.getItem(ConfigService.CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
